import CloseRoundedIcon from '@mui/icons-material/CloseRounded'
import NorthEastRoundedIcon from '@mui/icons-material/NorthEastRounded'
import { useTranslation } from 'next-i18next'
import Link from 'next/link'
import React from 'react'
import useLastSearch from 'src/hooks/useLastSearch'
import ga from 'src/utils/pixels/gtag'

/**
 * Soft resume card rendered above the hero search bar when a returning
 * visitor has a stored last-search entry. Read-only on home: writes happen
 * elsewhere (pillar-pill click handlers in Hero). Dismissable for the
 * session.
 *
 * Renders nothing on first paint and on visitors with no stored entry.
 * Backdrop-blur tint keeps it visible against the dark editorial hero
 * without competing with the H1.
 *
 * Layout-shift contract: "renders nothing on first paint" is NOT the same as
 * "no layout shift" — that assumption is what let this card push 82px of hero
 * content (and the background photo's crop) down mid-load for every returning
 * visitor until 2026-08-11. The height is fixed to `--resume-card-h` so it
 * exactly fills the slot reserved before first paint by `.resume-card-slot`
 * + `RESUME_RESERVATION_SCRIPT` (both in Landing/Hero.tsx). Keep the content
 * single-line (`truncate`); a second line breaks the reservation.
 */
const LastSearchCard: React.FC = () => {
  const { t } = useTranslation('landing')
  const { lastSearch, dismissLastSearch } = useLastSearch()

  if (!lastSearch) return null

  const locationLabel = lastSearch.hoodName
    ? `${lastSearch.hoodName}, ${lastSearch.cityName}`
    : lastSearch.cityName

  return (
    <div className="flex h-(--resume-card-h) items-center gap-2 rounded-xl border border-white-500/15 bg-white-500/[0.06] px-3 py-2.5 backdrop-blur-sm md:px-4 md:py-3">
      <Link
        href={lastSearch.href}
        onClick={() =>
          ga.event('home_section_cta_click', {
            section: 'last_search',
            cta: 'resume',
            pillar: lastSearch.pillar,
            city: lastSearch.city
          })
        }
        className="group flex min-w-0 flex-1 items-center gap-3"
      >
        <div className="flex min-w-0 flex-col">
          <span className="truncate font-display text-[10px] tracking-[0.2em] text-blue-200/70 uppercase">
            {t('INDEX_HERO.LAST_SEARCH.EYEBROW')}
          </span>
          <span className="truncate text-sm font-medium text-white-500/95 md:text-[15px]">
            {`${lastSearch.pillarLabel.toLowerCase()} en `}
            <span className="font-display italic">{locationLabel}</span>
          </span>
        </div>
        <span className="ml-auto inline-flex shrink-0 items-center gap-1 text-[11px] tracking-[0.08em] text-blue-300 uppercase transition-transform group-hover:translate-x-0.5">
          {/* "Continuar" hides on mobile so the search-location row gets the
              full width — the arrow alone communicates the action. Reappears
              from md: where there's room. */}
          <span className="hidden md:inline">
            {t('INDEX_HERO.LAST_SEARCH.ACTION')}
          </span>
          <NorthEastRoundedIcon className="text-[14px]" fontSize="inherit" />
        </span>
      </Link>
      <button
        type="button"
        onClick={() => {
          ga.event('home_section_cta_click', {
            section: 'last_search',
            cta: 'dismiss',
            pillar: lastSearch.pillar,
            city: lastSearch.city
          })
          dismissLastSearch()
        }}
        aria-label={t('INDEX_HERO.LAST_SEARCH.DISMISS')}
        className="ml-1 inline-flex size-7 shrink-0 items-center justify-center rounded-full text-white-500/60 transition-colors hover:bg-white-500/10 hover:text-white-500"
      >
        <CloseRoundedIcon className="text-[16px]" fontSize="inherit" />
      </button>
    </div>
  )
}

export default React.memo(LastSearchCard)
